import {
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ElementRef,
  ReactNode,
  forwardRef,
} from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'

import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { classNames } from '@/utils/helpers.ts'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    iconType?: 'single' | 'double'
    testId?: string
  }
>(({ className, children, iconType, testId, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={classNames(
      'placeholder:text-muted-foreground flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-left text-sm ring-offset-background  disabled:cursor-not-allowed disabled:opacity-50 dark:text-white',
      className,
    )}
    data-testid={testId}
    {...props}
  >
    <div className="flex-1 truncate">{children}</div>
    <SelectPrimitive.Icon asChild>
      {iconType === 'double' ? (
        <div className="flex flex-shrink-0 flex-col">
          <ExpandMoreIcon className="h-3 w-4 rotate-180 text-title" />
          <ExpandMoreIcon className="h-3 w-4 text-title" />
        </div>
      ) : (
        <ExpandMoreIcon className="h-4 w-4 flex-shrink-0 text-title" />
      )}
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))

SelectTrigger.displayName = 'SelectTrigger'

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    header?: ReactNode
    viewPortClassName?: string
    testId?: string
  }
>(
  (
    {
      className,
      viewPortClassName,
      children,
      position = 'popper',
      header,
      testId,
      ...props
    },
    ref,
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={classNames(
          'relative z-100 min-w-[8rem] overflow-hidden rounded-md border bg-card shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        data-testid={testId}
        {...props}
      >
        {header}
        <SelectPrimitive.Viewport
          className={classNames(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
            viewPortClassName,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
)

SelectContent.displayName = 'SelectContent'

const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    style?: CSSProperties
  }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={classNames(
      "relative flex-1 cursor-pointer select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none hover:bg-secondary-50 data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50 dark:text-white hover:dark:bg-secondary-600",
      className,
      props.disabled && 'pointer-events-none opacity-50 dark:text-white',
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))

SelectItem.displayName = 'SelectItem'

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
}
